import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Images, ArrowLeft, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";

type Album = {
  id: number;
  title: string;
  description: string;
  cover: string;
  images: string[];
};

const albums: Album[] = [
  {
    id: 1,
    title: "Školní akce 2024",
    description: "Fotografie z naší školní akce",
    cover: "/images/album1/cover.jpg",
    images: [
      "/images/album1/photo1.jpg",
      "/images/album1/photo2.jpg",
      "/images/album1/photo3.jpg",
    ],
  },
  {
    id: 2,
    title: "Vystoupení žáků",
    description: "Hudební vystoupení našich žáků",
    cover: "/images/album2/cover.jpg",
    images: ["/images/album2/photo1.jpg", "/images/album2/photo2.jpg"],
  },
];

export default function Gallery() {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("http://localhost:3000")
      .then((res) => res.json())
      .then((data: { title: string; images: string }[]) => {
        const mapped = data.map((item, index) => {
          const imageArray = item.images
            ? item.images.split(";").map((img) => img.trim()).filter(Boolean)
            : [];

          return {
            id: index + 1,
            title: item.title,
            description: "", // you can adjust if backend sends description
            cover: imageArray.length > 0 ? imageArray[0] : "",
            images: imageArray,
          };
        });
        setAlbums(mapped);
      })
      .catch((err) => {
        console.error("Error fetching albums:", err);
        setError("Unable to load pictures.");
      });
  }, []);

  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () =>
    setLightboxIndex((i) =>
      i !== null && selectedAlbum
        ? (i - 1 + selectedAlbum.images.length) % selectedAlbum.images.length
        : i
    );
  const nextImage = () =>
    setLightboxIndex((i) =>
      i !== null && selectedAlbum
        ? (i + 1) % selectedAlbum.images.length
        : i
    );

  return (
    <main className="flex flex-col items-center py-32 px-4 bg-gray-100">
      <div className="space-y-6 w-full max-w-6xl">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight flex items-center justify-center gap-2">
            <Images className="h-10 w-10" />
            Galerie
          </h1>
          <p className="text-muted-foreground mt-2">
            Prohlédněte si všechny naše alba a fotografie
          </p>
        </div>

        {/* Album list */}
        {!selectedAlbum && (
          <motion.div
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
            }}
          >
            {error || albums.length === 0 ? (
              <div className="col-span-full text-center text-gray-500">
                Zatím žádné obrázky
              </div>
            ) : (
              albums.map((album) => (
                <motion.div
                  key={album.id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.5 }}
                >
                  <Card
                    className="overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                    onClick={() => setSelectedAlbum(album)}
                  >
                    <div className="relative h-48 w-full">
                      <img
                        src={album.cover}
                        alt={album.title}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h2 className="text-lg font-semibold">{album.title}</h2>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </motion.div>
        )}

        {/* Album detail */}
        {selectedAlbum && (
          <div className="space-y-6">
            <button
              onClick={() => setSelectedAlbum(null)}
              className="flex items-center gap-2 text-blue-600 hover:underline"
            >
              <ArrowLeft className="h-5 w-5" />
              Zpět na alba
            </button>
            <h2 className="text-2xl font-bold">{selectedAlbum.title}</h2>
            <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
              {selectedAlbum.images.map((img, idx) => (
                <div
                  key={idx}
                  className="overflow-hidden rounded-xl shadow-lg hover:scale-105 transition-transform cursor-pointer"
                  onClick={() => setLightboxIndex(idx)}
                >
                  <img
                    src={img}
                    alt={`${selectedAlbum.title} - ${idx + 1}`}
                    className="object-cover w-full h-64"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {lightboxIndex !== null && selectedAlbum && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white text-3xl"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Prev button */}
          <button
            onClick={prevImage}
            className="absolute left-6 text-white text-3xl"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          {/* Next button */}
          <button
            onClick={nextImage}
            className="absolute right-6 text-white text-3xl"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          {/* Image */}
          <motion.img
            key={lightboxIndex}
            src={selectedAlbum.images[lightboxIndex]}
            alt="Expanded"
            className="max-h-[80%] max-w-[90%] object-contain rounded-xl shadow-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      )}
    </main>
  );
}
